/**
 * ChequeSettingsCard - Displays a single cheque settings configuration
 */

import type { ChequeSettingsPublic } from '@garden-platform/shared';
import type * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export interface ChequeSettingsCardProps {
  chequeSettings: ChequeSettingsPublic;
  onEdit: (settings: ChequeSettingsPublic) => void;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
}

const chequeStyleLabels: Record<string, string> = {
  standard: 'Standard',
  voucher: 'Voucher',
  wallet: 'Wallet',
};

export const ChequeSettingsCard: React.FC<ChequeSettingsCardProps> = ({
  chequeSettings,
  onEdit,
  onDelete,
  isDeleting = false,
}) => {
  // Build signature names list
  const signatureNames: string[] = [];
  if (chequeSettings.primarySignature) {
    signatureNames.push(chequeSettings.primarySignature.name);
  }
  if (chequeSettings.secondarySignature) {
    signatureNames.push(chequeSettings.secondarySignature.name);
  }

  return (
    <Card className={!chequeSettings.isActive ? 'opacity-60' : undefined}>
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{chequeSettings.name}</h4>
            {!chequeSettings.isActive && (
              <Badge variant="outline" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {chequeSettings.bankAccount?.bankName || 'No bank account'}
          </p>
        </div>
        <Badge variant="outline">
          {chequeStyleLabels[chequeSettings.chequeStyle] || chequeSettings.chequeStyle}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Current Cheque #</p>
            <p className="font-mono">{chequeSettings.currentChequeNumber}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Print Signature</p>
            <p>{chequeSettings.printSignature ? 'Yes' : 'No'}</p>
          </div>
          {chequeSettings.printSignature && signatureNames.length > 0 && (
            <div className="col-span-2">
              <p className="text-muted-foreground">Configured Signatures</p>
              <p>{signatureNames.join(', ')}</p>
            </div>
          )}
          {chequeSettings.requireDualSignatureAbove && (
            <div className="col-span-2">
              <p className="text-muted-foreground">Dual Signature Required Above</p>
              <p>${chequeSettings.requireDualSignatureAbove.toLocaleString()}</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(chequeSettings)}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(chequeSettings.id)}
            disabled={isDeleting}
            className="text-red-600 hover:text-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

ChequeSettingsCard.displayName = 'ChequeSettingsCard';
